// ---------------------------------------------------------------------------
// DSS-9: Text shadow builder — offsets, blur, color, presets, live preview
// ---------------------------------------------------------------------------
import { useState, useCallback, useId } from "react";
import { Type } from "lucide-react";

// ── Types ──
export interface TextShadowState {
  readonly hOffset: number;
  readonly vOffset: number;
  readonly blur: number;
  readonly color: string;
}

// ── Constants ──
const MIN_OFFSET = -50;
const MAX_OFFSET = 50;
const MIN_BLUR = 0;
const MAX_BLUR = 50;
const DEFAULT_COLOR = "#000000";

const PRESETS: readonly { readonly name: string; readonly value: string }[] = [
  { name: "None", value: "none" },
  { name: "Subtle", value: "0 1px 2px rgba(0,0,0,0.15)" },
  { name: "Medium", value: "0 2px 4px rgba(0,0,0,0.3)" },
  { name: "Strong", value: "2px 3px 6px rgba(0,0,0,0.5)" },
  { name: "Glow", value: "0 0 12px rgba(59,130,246,0.7)" },
];

// ── Props ──
interface TextShadowBuilderProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

// ── Parsing ──
function parseTextShadow(raw: string): TextShadowState {
  if (!raw || raw === "none") {
    return { hOffset: 0, vOffset: 0, blur: 0, color: DEFAULT_COLOR };
  }

  // Match pattern: <color>? <h> <v> <blur>? <color>?
  // Simple approach: extract numbers and the rest is color
  const nums = raw.match(/-?\d+(\.\d+)?/g);
  if (!nums || nums.length < 2) {
    return { hOffset: 0, vOffset: 0, blur: 0, color: DEFAULT_COLOR };
  }

  const hOffset = parseInt(nums[0] ?? "0", 10);
  const vOffset = parseInt(nums[1] ?? "0", 10);
  const blur = nums.length >= 3 ? parseInt(nums[2] ?? "0", 10) : 0;

  // Try to extract color (rgba, hex, or named)
  const colorMatch = raw.match(/(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\))/);
  const color = colorMatch ? colorMatch[0] : DEFAULT_COLOR;

  return { hOffset, vOffset, blur, color };
}

function buildTextShadow(state: TextShadowState): string {
  if (state.hOffset === 0 && state.vOffset === 0 && state.blur === 0) {
    return "none";
  }
  return `${state.hOffset}px ${state.vOffset}px ${state.blur}px ${state.color}`;
}

// ── Component ──
export default function TextShadowBuilder({
  value,
  onChange,
}: TextShadowBuilderProps) {
  const uid = useId();
  const hId = `${uid}-h`;
  const vId = `${uid}-v`;
  const blurId = `${uid}-blur`;
  const colorId = `${uid}-color`;

  const [state, setState] = useState<TextShadowState>(() =>
    parseTextShadow(value),
  );

  const update = useCallback(
    (patch: Partial<TextShadowState>) => {
      setState((prev) => {
        const next = { ...prev, ...patch };
        onChange(buildTextShadow(next));
        return next;
      });
    },
    [onChange],
  );

  const handlePreset = useCallback(
    (presetValue: string) => {
      onChange(presetValue);
      setState(parseTextShadow(presetValue));
    },
    [onChange],
  );

  const isPresetActive = (presetValue: string): boolean => {
    if (presetValue === "none") return value === "none" || !value;
    return value === presetValue;
  };

  // Hex color for the input (strip alpha from rgba if needed)
  const hexColor = state.color.startsWith("#")
    ? state.color.slice(0, 7)
    : DEFAULT_COLOR;

  return (
    <div className="flex flex-col gap-3">
      {/* Header */}
      <div className="flex items-center gap-2 text-xs font-semibold text-[var(--color-foreground)]">
        <Type className="h-3.5 w-3.5" />
        Text Shadow
      </div>

      {/* Presets */}
      <div className="flex flex-wrap gap-1">
        {PRESETS.map((preset) => (
          <button
            key={preset.name}
            type="button"
            onClick={() => handlePreset(preset.value)}
            className={[
              "rounded-md border px-2.5 py-1 text-[10px] font-medium",
              isPresetActive(preset.value)
                ? "border-[var(--color-primary)] bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "border-[var(--color-border)] text-[var(--color-muted-foreground)] hover:border-[var(--color-primary)]/50",
            ].join(" ")}
          >
            {preset.name}
          </button>
        ))}
      </div>

      {/* Live preview */}
      <div
        className="flex h-20 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-card)]"
        style={{ overflow: "hidden" }}
      >
        <span
          className="text-2xl font-bold text-[var(--color-foreground)]"
          style={{ textShadow: value === "none" ? undefined : value }}
        >
          Preview Text
        </span>
      </div>

      {/* Controls */}
      <div className="flex flex-col gap-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-muted)]/40 p-3">
        {/* H Offset */}
        <SliderRow
          id={hId}
          label="H Offset"
          min={MIN_OFFSET}
          max={MAX_OFFSET}
          value={state.hOffset}
          unit="px"
          onChange={(v) => update({ hOffset: v })}
        />

        {/* V Offset */}
        <SliderRow
          id={vId}
          label="V Offset"
          min={MIN_OFFSET}
          max={MAX_OFFSET}
          value={state.vOffset}
          unit="px"
          onChange={(v) => update({ vOffset: v })}
        />

        {/* Blur */}
        <SliderRow
          id={blurId}
          label="Blur"
          min={MIN_BLUR}
          max={MAX_BLUR}
          value={state.blur}
          unit="px"
          onChange={(v) => update({ blur: v })}
        />

        {/* Color */}
        <div className="flex items-center gap-2">
          <label
            htmlFor={colorId}
            className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
          >
            Color
          </label>
          <input
            id={colorId}
            type="color"
            value={hexColor}
            onChange={(e) => update({ color: e.target.value })}
            className="h-6 w-8 cursor-pointer rounded border border-[var(--color-border)] bg-transparent p-0"
          />
          <span className="text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
            {state.color}
          </span>
        </div>
      </div>

      {/* CSS output */}
      <div className="rounded-md bg-[var(--color-muted)] px-3 py-2">
        <code className="text-[10px] text-[var(--color-muted-foreground)]">
          text-shadow: {value || "none"};
        </code>
      </div>
    </div>
  );
}

// ── Reusable slider row ──
interface SliderRowProps {
  readonly id: string;
  readonly label: string;
  readonly min: number;
  readonly max: number;
  readonly value: number;
  readonly unit: string;
  readonly onChange: (value: number) => void;
}

function SliderRow({
  id,
  label,
  min,
  max,
  value: val,
  unit,
  onChange: onSlide,
}: SliderRowProps) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={id}
        className="w-16 shrink-0 text-[10px] font-medium text-[var(--color-muted-foreground)]"
      >
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={1}
        value={val}
        onChange={(e) => onSlide(Number(e.target.value))}
        className="h-1 flex-1 cursor-pointer appearance-none rounded-full bg-[var(--color-border)] accent-[var(--color-primary)]"
      />
      <span className="w-12 text-right text-[10px] tabular-nums text-[var(--color-muted-foreground)]">
        {val}{unit}
      </span>
    </div>
  );
}
